import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { addDays, fmtDate, inr, nightsBetween, todayISO } from "@/lib/format";
import useSeo from "@/hooks/useSeo";
import { useConfig } from "@/hooks/useConfig";
import RoomSelector from "@/components/booking/RoomSelector";
import GuestForm from "@/components/booking/GuestForm";
import PriceSummary from "@/components/PriceSummary";

const Step = ({ n, title, children }) => (
  <section className="bg-white border border-[#E2DCD0] p-5 sm:p-8">
    <p className="eyebrow text-[#C28B5E]">Step {n}</p>
    <h2 className="mt-1 text-2xl sm:text-3xl text-[#1E3B2E]">{title}</h2>
    <div className="mt-6">{children}</div>
  </section>
);

const num = (v, d) => (Number.isFinite(+v) && v !== null && v !== "" ? +v : d);

export default function Book() {
  useSeo("Book Your Stay", "Book The Ananta Udaipur direct. Check live availability, select multiple room categories and pay securely via UPI.");
  const [params] = useSearchParams();
  const nav = useNavigate();
  const cfg = useConfig();
  const today = todayISO();
  const [properties, setProperties] = useState([]);
  const [slug, setSlug] = useState(params.get("property") || "udaipur");
  const [prop, setProp] = useState(null);
  const ciParam = params.get("check_in");
  const [dates, setDates] = useState({ check_in: ciParam && ciParam >= today ? ciParam : addDays(today, 1), check_out: params.get("check_out") || addDays(ciParam && ciParam >= today ? ciParam : today, ciParam ? 1 : 2) });
  const [adults, setAdults] = useState(num(params.get("adults"), 2));
  const [children, setChildren] = useState(num(params.get("children"), 0));
  const roomsWanted = num(params.get("rooms"), 1);
  const [qty, setQty] = useState(params.get("room") ? { [params.get("room")]: 1 } : {});
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [guest, setGuest] = useState({ name: "", email: "", phone: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const nights = nightsBetween(dates.check_in, dates.check_out);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data.filter((p) => !p.coming_soon)));
  }, []);
  useEffect(() => {
    api.get(`/properties/${slug}`).then((r) => setProp(r.data)).catch(() => setProp(null));
  }, [slug]);
  useEffect(() => {
    if (nights < 1 || dates.check_in < today) { setAvail(null); return; }
    setLoading(true);
    api.post("/availability", { property_slug: slug, ...dates })
      .then((r) => setAvail(r.data.available))
      .catch((e) => { setAvail(null); toast.error(errMsg(e)); })
      .finally(() => setLoading(false));
  }, [slug, dates, nights, today]);
  useEffect(() => {
    if (!avail) return;
    setQty((q) => Object.fromEntries(Object.entries(q).map(([k, v]) => [k, Math.min(v, avail[k] ?? 0)])));
  }, [avail]);

  const lines = useMemo(() => (prop?.rooms || []).filter((r) => qty[r.slug] > 0).map((r) => ({ slug: r.slug, name: r.name, price: r.price ?? null, qty: qty[r.slug], capacity: r.capacity })), [prop, qty]);
  const quote = lines.some((l) => l.price === null);
  const totalRooms = lines.reduce((s, l) => s + l.qty, 0);
  const capacity = lines.reduce((s, l) => s + l.capacity * l.qty, 0);
  const guests = adults + children;
  const capacityOk = totalRooms > 0 && guests <= capacity && adults >= totalRooms;
  const sub = lines.reduce((s, l) => s + (l.price || 0) * l.qty * Math.max(nights, 0), 0);
  const mobileTotal = sub - (cfg.offer?.active ? Math.round((sub * cfg.offer.percent) / 100) : 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!capacityOk) return toast.error(totalRooms === 0 ? "Please select at least one room" : guests > capacity ? `Selected rooms fit up to ${capacity} guests. Please add a room.` : "Each room needs at least one adult");
    if (!consent) return toast.error("Please accept the policies to continue");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", { property_slug: slug, ...dates, adults, children, rooms: lines.map(({ slug: s, qty: q }) => ({ slug: s, qty: q })), guest, consent });
      nav(`/booking/${data.booking_id}?t=${data.token}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  const setD = (k) => (e) => {
    const v = e.target.value;
    setDates((d) => (k === "check_in" && d.check_out <= v ? { check_in: v, check_out: addDays(v, 1) } : { ...d, [k]: v }));
  };

  return (
    <div className="bg-[#F4EFE6] min-h-screen py-10 sm:py-16 pb-28 lg:pb-16">
      <form onSubmit={submit} className="container-x grid lg:grid-cols-12 gap-6 lg:gap-10 items-start" data-testid="booking-form">
        <div className="lg:col-span-8 space-y-6">
          <div>
            <p className="eyebrow text-[#C28B5E]">Reservations</p>
            <h1 className="mt-2 text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Book your stay</h1>
          </div>
          <Step n={1} title="Dates & guests">
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
              <label className="col-span-2 sm:col-span-6 flex flex-col gap-1.5"><span className="eyebrow !text-[10px] text-[#6b6b6b]">Hotel</span>
                <select className="field" value={slug} onChange={(e) => { setSlug(e.target.value); setQty({}); }} data-testid="booking-property-select">
                  {(properties.length ? properties : [{ slug: "udaipur", name: "The Ananta Udaipur" }]).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </label>
              <label className="col-span-1 sm:col-span-2 flex flex-col gap-1.5"><span className="eyebrow !text-[10px] text-[#6b6b6b]">Check-in</span><input type="date" className="field" min={today} value={dates.check_in} onChange={setD("check_in")} required data-testid="booking-checkin-input" /></label>
              <label className="col-span-1 sm:col-span-2 flex flex-col gap-1.5"><span className="eyebrow !text-[10px] text-[#6b6b6b]">Check-out</span><input type="date" className="field" min={addDays(dates.check_in, 1)} value={dates.check_out} onChange={setD("check_out")} required data-testid="booking-checkout-input" /></label>
              <label className="flex flex-col gap-1.5"><span className="eyebrow !text-[10px] text-[#6b6b6b]">Adults</span><input type="number" min={1} max={40} className="field" value={adults} onChange={(e) => setAdults(Math.max(1, +e.target.value || 1))} data-testid="booking-adults-input" /></label>
              <label className="flex flex-col gap-1.5"><span className="eyebrow !text-[10px] text-[#6b6b6b]">Children</span><input type="number" min={0} max={20} className="field" value={children} onChange={(e) => setChildren(Math.max(0, +e.target.value || 0))} data-testid="booking-children-input" /></label>
            </div>
            <p className="mt-4 text-sm text-[#3b3b3b]" data-testid="booking-nights">{nights > 0 ? `${fmtDate(dates.check_in)} → ${fmtDate(dates.check_out)} · ${nights} night${nights > 1 ? "s" : ""}` : "Check-out must be after check-in"}</p>
          </Step>
          <Step n={2} title="Choose your rooms">
            <p className="text-sm text-[#6b6b6b] mb-4">Mix and match categories in one booking. {roomsWanted > 1 && `You searched for ${roomsWanted} rooms — `}Selected: <strong data-testid="booking-rooms-selected">{totalRooms}</strong> room{totalRooms !== 1 && "s"} for up to {capacity} guests.</p>
            {prop && <RoomSelector rooms={prop.rooms} qty={qty} setQty={setQty} available={avail} loading={loading} />}
            {totalRooms > 0 && !capacityOk && <p className="mt-4 text-sm text-red-700" data-testid="booking-capacity-warning">{guests > capacity ? `These rooms accommodate up to ${capacity} guests (extra bed included). Please add another room.` : "Each room needs at least one adult."}</p>}
          </Step>
          <Step n={3} title="Guest details"><GuestForm guest={guest} setGuest={setGuest} consent={consent} setConsent={setConsent} /></Step>
        </div>
        <aside className="lg:col-span-4 lg:sticky lg:top-24">
          <div className="bg-white border border-[#E2DCD0] p-6 sm:p-8">
            <p className="eyebrow text-[#C28B5E]">Your booking</p>
            <p className="mt-2 font-['Cormorant_Garamond'] text-2xl text-[#1E3B2E]">{prop?.name}</p>
            <p className="text-xs text-[#6b6b6b] mt-1 mb-4">{nights > 0 && `${nights} night${nights > 1 ? "s" : ""} · `}{adults} adult{adults > 1 && "s"}{children > 0 && `, ${children} child${children > 1 ? "ren" : ""}`}</p>
            <PriceSummary lines={lines} nights={Math.max(nights, 0)} offer={cfg.offer} />
            <button type="submit" disabled={submitting || !capacityOk || nights < 1} className="btn-primary w-full mt-6 !h-14" data-testid="booking-submit-btn">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : quote ? "Send booking request" : "Continue to payment"}
            </button>
            <p className="mt-3 text-[11px] text-[#6b6b6b] text-center">{quote ? "The hotel will confirm your room rate by email; you then pay securely via UPI." : `Rooms are held for ${cfg.hold_hours || 24} hours while you complete the UPI payment.`}</p>
            <p className="mt-3 text-[11px] text-[#6b6b6b] text-center" data-testid="booking-checkin-info">Check-in {cfg.checkin_time || "15:00 hrs"} · Check-out {cfg.checkout_time || "11:00 hrs"}{cfg.booking_note ? ` · ${cfg.booking_note}` : ""}</p>
          </div>
        </aside>
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E2DCD0] px-5 py-3 flex items-center justify-between gap-4" data-testid="booking-mobile-bar">
          <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#6b6b6b]">{totalRooms} room{totalRooms !== 1 && "s"} · {Math.max(nights, 0)} night{nights !== 1 && "s"}</p><p className="font-['Cormorant_Garamond'] text-2xl text-[#1E3B2E]" data-testid="booking-mobile-total">{quote ? "On request" : inr(mobileTotal)}</p></div>
          <button type="submit" disabled={submitting || !capacityOk || nights < 1} className="btn-primary !px-5" data-testid="booking-mobile-submit-btn">{quote ? "Send request" : "Continue"}</button>
        </div>
      </form>
    </div>
  );
}
